import {
  Button,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import IconCheck from "@material-ui/icons/Check";
import IconUncheck from "@material-ui/icons/Close";
import IconManager from "@material-ui/icons/Group";
import IconInfo from "@material-ui/icons/Info";
import IconInfoOutlined from "@material-ui/icons/InfoOutlined";
import IconArrowDown from "@material-ui/icons/KeyboardArrowDown";
import IconArrowUp from "@material-ui/icons/KeyboardArrowUp";
import IconLaunch from "@material-ui/icons/Launch";
import IconAgent from "@material-ui/icons/Person";
import IconCustomer from "@material-ui/icons/PersonOutline";
import React, { useEffect, useReducer, useState } from "react";
import { connect } from "react-redux";
import { getUsersBelowMe } from "../actions";
import { dispatchErrorMessage } from "../actions/Alert";
import HeaderPageItem from "../custom/HeaderPageItem";
import PageContainer, { CardPageItem, PageItem } from "../custom/Page";
import { isAgent, isAgentManager, isAgentOf, Roles } from "../util";

function ReportUserContracts({ currentUser, dispatch }) {
  const [loading, setLoading] = useState(true);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [managers, setManagers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const classes = useStyles();

  function isAgentManagerBelow(manager, user) {
    return isAgentManager(manager) && isAgentOf(user, manager);
  }

  function isAgentManagerBelowId(manager, id) {
    return isAgentManager(manager) && manager.consultant.id === id;
  }

  function isAgentBelow(agent, user) {
    return isAgent(agent) && isAgentOf(user, agent);
  }

  function isCustomerBelow(customer, user) {
    return customer.role !== Roles.Agent && isAgentOf(user, customer);
  }

  function SimplifyUserClone(u) {
    let consultant = null;
    if (u.consultant) {
      consultant = SimplifyUserClone(u.consultant);
    }
    let contractAgent = null;
    if (u.contractAgent) {
      contractAgent = { id: u.contractAgent.id, type: u.contractAgent.type, status: u.contractAgent.status };
    }
    return {
      id: u.id,
      name: u.name,
      role: u.role,
      email: u.email,
      apt: u.apt,
      contractAgent,
      consultant,
    };
  }

  function SimplifyUser(u) {
    if (u.consultant) {
      u.consultant = SimplifyUserClone(u.consultant);
    }
    if (u.contractAgent) {
      u.contractAgent = { id: u.contractAgent.id, type: u.contractAgent.type, status: u.contractAgent.status };
    }
    for (const p in u) {
      if (!["id", "name", "role", "email", "apt", "consultant", "contractAgent"].includes(p)) {
        delete u[p];
      }
    }
    return u;
  }

  function AddUIData(u) {
    u.ui = {
      isSelected: false,
      isInfoOpen: false,
      isListOpen: false,
    };
    return u;
  }

  function filterAndFillBelow(allUsers, filterFunc) {
    const users = allUsers.filter(filterFunc);
    users.forEach((m) => {
      SimplifyUser(m);
      AddUIData(m);
      fillBellow(m, allUsers);
    });
    return users;
  }

  function fillBellow(user, allUsers) {
    user.managers = filterAndFillBelow(allUsers, (u) => isAgentManagerBelow(u, user));
    user.agents = filterAndFillBelow(allUsers, (u) => isAgentBelow(u, user));
    user.customers = filterAndFillBelow(allUsers, (u) => isCustomerBelow(u, user));
  }

  async function getReport() {
    try {
      console.time("loading");
      setLoading(true);
      const isAdmin = [Roles.Admin, Roles.FinanceManager, Roles.Kyc].includes(currentUser.role);
      const visibleToMe = await getUsersBelowMe();
      visibleToMe.sort((u1, u2) => u1.name.localeCompare(u2.name));
      const topId = isAdmin ? 1 : currentUser.id;
      setManagers(filterAndFillBelow(visibleToMe, (u) => !u.ui && isAgentManagerBelowId(u, topId)));
      setAgents(filterAndFillBelow(visibleToMe, (u) => !u.ui && isAgent(u)));
      if (!isAdmin) {
        setCustomers(filterAndFillBelow(visibleToMe, (u) => !u.ui && u.role !== Roles.Agent));
      }
      setLoading(false);
      console.timeEnd("loading");
    } catch (e) {
      dispatchErrorMessage(e.message, dispatch);
      setLoading(false);
    }
  }

  function effect() {
    getReport();
  }

  useEffect(effect, []);

  function setAllLists_(user, open) {
    user.ui.isListOpen = open && (user.managers.length > 0 || user.agents.length > 0 || user.customers.length > 0);
    user.managers.forEach((manager) => setAllLists_(manager, open));
    user.agents.forEach((agent) => setAllLists_(agent, open));
    user.customers.forEach((customer) => setAllLists_(customer, open));
  }

  function setAllLists(open) {
    managers.forEach((manager) => setAllLists_(manager, open));
    forceUpdate();
  }

  if (loading) return <CircularProgress style={{ marginTop: "25%" }} />;
  return (
    <PageContainer full>
      <HeaderPageItem title="app.report.userContracts" showBackButton destination="/reports" />
      <CardPageItem raised>
        <CardContent>
          <Button variant="outlined" size="small" onClick={(e) => setAllLists(true)}>
            Abrir todos
          </Button>{" "}
          <Button variant="outlined" size="small" onClick={(e) => setAllLists(false)}>
            Fechar todos
          </Button>
        </CardContent>
      </CardPageItem>
      <PageItem>
        {managers.length > 0 && (
          <>
            <Typography variant="caption" classes={{ root: classes.listTitle }}>
              Gestores
            </Typography>
            <List>
              {managers.map((manager) => (
                <UserListItem key={manager.id} user={manager} classes={classes} />
              ))}
            </List>
          </>
        )}
        {agents.length > 0 && (
          <>
            <Typography variant="caption" classes={{ root: classes.listTitle }}>
              Agentes
            </Typography>
            <List>
              {agents.map((agent) => (
                <UserListItem key={agent.id} user={agent} classes={classes} />
              ))}
            </List>
          </>
        )}
        {customers.length > 0 && (
          <>
            <Typography variant="caption" classes={{ root: classes.listTitle }}>
              Clientes
            </Typography>
            <List>
              {customers.map((customer) => (
                <UserListItem key={customer.id} user={customer} classes={classes} />
              ))}
            </List>
          </>
        )}
      </PageItem>
    </PageContainer>
  );
}

function UserListItem({ user, classes, lines, bgIntensity = 50 }) {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [openInfo, setOpenInfo] = useState(false);
  const hasManagers = user.managers.length > 0;
  const hasAgents = user.agents.length > 0;
  const hasCustomers = user.customers.length > 0;
  const aptColor = { true: "green", false: "red" };
  bgIntensity -= 10;
  if (bgIntensity < 0) bgIntensity = 0;

  return (
    <>
      {lines && <hr style={{ border: "1px solid #333", padding: 0, margin: 0 }} />}
      <div
        style={{
          borderLeft: "1px solid #333",
          backgroundColor: user.ui.isListOpen ? `rgb(${bgIntensity},${bgIntensity},${bgIntensity})` : "unset",
        }}
      >
        <ListItem
          button={hasManagers || hasAgents || hasCustomers}
          onClick={() => {
            if (hasManagers || hasAgents || hasCustomers) {
              user.ui.isListOpen = !user.ui.isListOpen;
              forceUpdate();
            }
          }}
        >
          <ListItemIcon>
            <div>
              {isAgentManager(user) ? (
                <IconManager classes={{ root: classes.iconManager }} />
              ) : isAgent(user) ? (
                <IconAgent classes={{ root: classes.iconAgent }} />
              ) : (
                <IconCustomer classes={{ root: classes.iconCustomer }} />
              )}
              {user.role === Roles.Agent &&
                (user.contractAgent?.status === "ACTIVE" ? (
                  <IconCheck
                    style={{ fontSize: "100%", color: "lime", marginLeft: -6, marginTop: -2, position: "absolute" }}
                  />
                ) : (
                  <IconUncheck
                    style={{ fontSize: "100%", color: "red", marginLeft: -6, marginTop: -2, position: "absolute" }}
                  />
                ))}
            </div>
          </ListItemIcon>
          <ListItemText
            primary={
              <>
                {user.name} <small style={{ color: "#999" }}>({user.id})</small>
              </>
            }
          />
          <ListItemSecondaryAction>
            {(hasManagers || hasAgents || hasCustomers) && (
              <IconButton
                size="small"
                onClick={() => {
                  user.ui.isListOpen = !user.ui.isListOpen;
                  forceUpdate();
                }}
              >
                {user.ui.isListOpen ? <IconArrowUp /> : <IconArrowDown />}
              </IconButton>
            )}
            <IconButton size="small" onClick={() => setOpenInfo(!openInfo)} style={{ color: "#999" }}>
              {openInfo ? (
                <IconInfo style={{ color: aptColor[user.apt] }} />
              ) : (
                <IconInfoOutlined style={{ color: aptColor[user.apt] }} />
              )}
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
        <Collapse in={openInfo} timeout="auto" mountOnEnter unmountOnExit>
          <div
            style={{
              display: "flex",
              paddingLeft: 75,
              paddingTop: 0,
              paddingBottom: 8,
              paddingRight: 16,
              justifyContent: "space-between",
              color: "#999",
            }}
          >
            <div>
              <Typography>{user.email}</Typography>
              <Typography>
                {isAgentManager(user) ? "AGENT MANAGER" : user.role} -{" "}
                <small style={{ color: aptColor[user.apt] }}>{user.apt ? "apto" : "inapto"}</small>
                {user.role === Roles.Agent && (
                  <>
                    {" "}
                    -{" "}
                    <small style={{ color: aptColor[user.contractAgent?.status === "ACTIVE"] }}>
                      agreement: {user.contractAgent?.status}
                    </small>
                  </>
                )}
              </Typography>
            </div>
            <div>
              <IconButton size="small" href={"/user/" + user.id} target="_blank">
                <IconLaunch />
              </IconButton>
            </div>
          </div>
        </Collapse>
        <Collapse in={user.ui.isListOpen} timeout="auto" mountOnEnter unmountOnExit style={{ marginLeft: "5%" }}>
          {hasManagers && (
            <>
              <Typography variant="caption" classes={{ root: classes.listTitle }}>
                Gestores
              </Typography>
              <List>
                {user.managers.map((manager) => (
                  <UserListItem key={manager.id} user={manager} classes={classes} bgIntensity={bgIntensity} />
                ))}
              </List>
            </>
          )}
          {hasAgents && (
            <>
              <Typography variant="caption" classes={{ root: classes.listTitle }}>
                Agentes
              </Typography>
              <List>
                {user.agents.map((agent) => (
                  <UserListItem key={agent.id} user={agent} classes={classes} bgIntensity={bgIntensity} />
                ))}
              </List>
            </>
          )}
          {hasCustomers && (
            <>
              <Typography variant="caption" classes={{ root: classes.listTitle }}>
                Clientes
              </Typography>
              <List>
                {user.customers.map((customer) => (
                  <UserListItem key={customer.id} user={customer} classes={classes} bgIntensity={bgIntensity} />
                ))}
              </List>
            </>
          )}
        </Collapse>
      </div>
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  mainHeaderRow: {
    "& > th": {
      //fontWeight: "bold",
      borderBottom: "3px solid #333",
      color: "#AAA",
    },
  },
  mainRow: {
    "& > *": {
      borderBottom: "unset",
    },
    "& > td:first-child": {
      width: 10,
      paddingLeft: 10,
      paddingRight: 0,
    },
  },
  detailBox: {
    backgroundColor: "#151515",
    paddingLeft: 50,
  },
  detailHeaderRow: {
    "& > th": {
      //fontWeight: "bold",
      borderBottom: "3px solid #222",
      color: "#AAA",
    },
  },
  detailRow: {
    "& > td": { borderBottom: "1px solid #222" },
  },
  iconManager: {
    color: theme.palette.primary.main,
  },
  iconAgent: {
    color: "white",
  },
  iconCustomer: {
    color: "#666",
  },
  listTitle: {
    color: "#999",
    paddingLeft: 16,
  },
}));

export default connect((state) => ({ currentUser: state.user.user.me }))(ReportUserContracts);
